import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  private recoveryTimer?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Auto-recover from Chrome translate DOM manipulation errors
    // These happen when Chrome translates during React rendering
    if (error.message?.includes('insertBefore') || error.message?.includes('removeChild')) {
      console.warn('Chrome translate DOM error detected, will auto-recover');
      return { hasError: true, error };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Auto-recover from Chrome translate errors after a short delay
    if (error.message?.includes('insertBefore') || error.message?.includes('removeChild')) {
      this.recoveryTimer = setTimeout(() => {
        console.log('Auto-recovering from Chrome translate error...');
        this.setState({ hasError: false, error: undefined });
      }, 100);
    }
  }

  componentWillUnmount() {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <h1 className="text-xl font-medium text-foreground-strong mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">An error occurred while loading the booking page.</p>
            <details className="text-left text-sm text-muted-foreground">
              <summary className="cursor-pointer font-medium">Error details</summary>
              <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
                {this.state.error?.message}
                {this.state.error?.stack}
              </pre>
            </details>
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}